/**
 * Weight overlay — who owns the flesh, seen (ADR-027 stage 1).
 *
 * The week's law: "the armature is right, the deformation is wrong" is the
 * signature of an invisible thing — a bone's bind smear or a wrong frame —
 * and a week of headless instruments was the price of not seeing (ADR-027
 * §1). Allen's 2026-08-20 field read said it plainly: the graft took, the
 * bones are placed, and the thumb webbing still tears — *"the weights
 * assigned to the bones are simply not at all correct."* This layer makes
 * that claim checkable by looking.
 *
 * WHAT IT DRAWS. A GHOST SkinnedMesh over each of the subject's skinned
 * meshes: same geometry object, same skeleton, its own unlit vertex-colour
 * material. Two modes:
 *
 *  - `bone`: the selected bone's influence as a heat ramp (cold blue → red),
 *    with per-vertex ALPHA — flesh the bone does not touch stays fully
 *    transparent, so the texture underneath remains the thing being judged
 *    (ADR-027 D2). Small weights are deliberately loud: a 0.08 leak into the
 *    thumb webbing is the bug being hunted, so alpha floors at visibility
 *    rather than scaling to invisibility.
 *  - `shared`: every influence at once — each bone gets a stable hue and a
 *    vertex blends the hues of the bones that own it, weighted by their
 *    split. A cleanly-owned region reads as one flat colour; the thumb–palm
 *    web reads as the colour BATTLE it is. Web and cuff shear is always a
 *    shared-vertex story (ADR-027 §2.1), so the contested zones are the
 *    picture.
 *
 * WHY A GHOST AND NOT A MATERIAL EDIT. The renderer is WebGPU, where GLSL
 * `onBeforeCompile` patches are a silent no-op, and the D2 requirement is an
 * overlay that never swaps the material out from under the texture. A second
 * SkinnedMesh bound to the SAME skeleton rides the playing clip for free —
 * "live under the clip" costs nothing — and disposing it restores the world
 * exactly, because the subject was never touched.
 *
 * THE GHOST OWNS ITS GEOMETRY OBJECT; the heavy attributes are shared by
 * REFERENCE (position, normal, skin, index, morphs — no copies), and the
 * `color` attribute is the ghost's own. The first build painted the heat
 * straight onto the SUBJECT'S geometry, and two Codex P1s (#98) name the
 * price: an export serialized the diagnostic as the file's `COLOR_0`, and on
 * a source that already carried a real 3-component color attribute the RGBA
 * write ran one component past every vertex, corrupting its neighbour. The
 * diagnostic never touches what the user ships. Memory cost is unchanged —
 * one RGBA array per mesh, which is what the paint always needed.
 *
 * OPACITY IS THE D2 DIAL. At the default 0.85 the heat floats over the
 * texture, brighter than the first cut's 0.65 — under the wireframe the colour
 * map read washed out (Allen, 2026-09-02: "brighter — more opaque"); at 0.65 it floated over the
 * texture (the ADR's proposal); at 1.0 it IS Skinner's flat weight view (the
 * alternative). One slider covers both readings of D2 rather than choosing.
 *
 * FAIL-LOUD (§7): every show returns `null` on success or a REFUSAL STRING —
 * "bone not in the skeleton", "bone skins no vertices" — for the caller to
 * surface. It never leaves a blank or stale overlay standing for an answer.
 *
 * View-layer only — it draws what the geometry attributes already say and
 * decides nothing about them. Depends on: three. Depended on by: main
 * (wires it to pose-mode selection and the W key).
 */
import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  MeshBasicMaterial,
  Object3D,
  SkinnedMesh,
} from 'three';
import { isViewportDebris, markViewportDebris } from '@harryhausen/adapter-three';

export type WeightOverlayMode = 'off' | 'bone' | 'shared';

export interface WeightOverlay {
  /**
   * Heat-map one bone's influence. Null on success, else a refusal to surface.
   * `companions` are DERIVED bones whose weight is semantically this bone's —
   * ADR-028's `_corr` helpers: the crease band's flesh was transferred to the
   * helper, and the helper is hidden from the pose surface by design, so
   * without the union the elbow reads as a HOLE in its own driver's heat (the
   * field found it: "the W overlay on the elbow doesn't render"). Companions
   * absent from the skeleton are skipped — a rig without correctives is not
   * a refusal.
   */
  showBone(boneName: string, companions?: readonly string[]): string | null;
  /** Show every influence at once — ownership as colour, contests as blends. */
  showShared(): string | null;
  /** 0.15..1. ~0.85 floats over the texture; 1.0 is the flat Skinner look. */
  setOpacity(opacity: number): void;
  hide(): void;
  dispose(): void;
  /** Live evidence for probes and the status line. */
  probe(): {
    mode: WeightOverlayMode;
    bone: string | null;
    opacity: number;
    ghosts: number;
    /** Vertices the shown bone influences (bone mode), summed across meshes. */
    influenced: number;
  };
}

// Identity comes from adapter-three's viewport-debris vocabulary — the ONE
// source every door consults: exportGlb leaves ghosts out of user files,
// soleSkinnedMesh refuses to count them as the subject (Codex P1s, #98).
// Ghosts are stamped into a session WeakSet and carry NO scene-graph name
// (rounds seven through eleven walked every name-based identity off the
// board: user nodes wear our names, files forge userData, clips bind by
// name). The kind survives only as the label reports use (viewportDebrisLabel).
const DEFAULT_OPACITY = 0.85;
const MIN_OPACITY = 0.15;
/**
 * At (or past) this opacity the D2 dial means "Skinner's FLAT weight view" —
 * and flat must mean flat: the painted per-vertex alpha (the leak floor, the
 * 0.9 shared wash) is overridden to 1 on every influenced vertex, because a
 * material opacity of 1 multiplied under a 0.45 vertex alpha still showed
 * half the texture (Codex P2, #98 round two). Zero-weight flesh stays
 * transparent in either reading.
 */
const FLAT_OPACITY = 0.99;

/** Heat ramp: 0 → transparent; low blue → cyan → green → yellow → 1.0 red. */
function heat(weight: number): { r: number; g: number; b: number; a: number } {
  if (weight <= 0) return { r: 0, g: 0, b: 0, a: 0 };
  const t = Math.min(1, weight);
  // Four linear segments through blue(0)→cyan(.25)→green(.5)→yellow(.75)→red(1).
  let r = 0;
  let g = 0;
  let b = 0;
  if (t < 0.25) {
    b = 1;
    g = t / 0.25;
  } else if (t < 0.5) {
    g = 1;
    b = 1 - (t - 0.25) / 0.25;
  } else if (t < 0.75) {
    g = 1;
    r = (t - 0.5) / 0.25;
  } else {
    r = 1;
    g = 1 - (t - 0.75) / 0.25;
  }
  // The leak floor: the bug being hunted is a SMALL weight where none belongs,
  // so visibility must not scale away with the weight.
  const a = 0.45 + 0.55 * t;
  return { r, g, b, a };
}

/** A stable, well-separated hue per bone index — golden-angle walk. */
function boneHue(index: number): Color {
  return new Color().setHSL(((index * 137.508) % 360) / 360, 0.85, 0.55);
}

function skinnedMeshes(root: Object3D): SkinnedMesh[] {
  const found: SkinnedMesh[] = [];
  root.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh && !isViewportDebris(o)) found.push(o as SkinnedMesh);
  });
  return found;
}

export function createWeightOverlay(getScene: () => Object3D | null): WeightOverlay {
  interface Ghost {
    readonly source: SkinnedMesh;
    readonly mesh: SkinnedMesh;
    readonly material: MeshBasicMaterial;
  }
  let ghosts: Ghost[] = [];
  let mode: WeightOverlayMode = 'off';
  let shownBone: string | null = null;
  let shownCompanions: readonly string[] = [];
  let opacity = DEFAULT_OPACITY;
  let influenced = 0;

  function teardown(): void {
    for (const g of ghosts) {
      g.mesh.removeFromParent();
      g.material.dispose();
      // The ghost's geometry OBJECT is ours to dispose; the attributes it
      // shares by reference stay CPU-intact on the subject, which re-uploads
      // them transparently if the renderer dropped them with this dispose.
      // Only the color attribute was ever exclusively the ghost's.
      g.mesh.geometry.dispose();
    }
    ghosts = [];
    influenced = 0;
  }

  /** Build (or rebuild) one ghost per skinned mesh of the CURRENT subject. */
  function ensureGhosts(): string | null {
    const scene = getScene();
    // A refusal releases whatever ghosts an EARLIER subject built (Codex P2,
    // #98 round four) — refusing while still holding the old model's meshes
    // would keep its whole object graph alive behind a hidden overlay.
    if (!scene) {
      teardown();
      return 'no subject loaded';
    }
    const sources = skinnedMeshes(scene);
    if (sources.length === 0) {
      teardown();
      return 'subject has no skinned mesh';
    }
    // A subject swap leaves ghosts bound to a dead skeleton — rebuild when the
    // sources changed, not just when the count did.
    const stale =
      ghosts.length !== sources.length || ghosts.some((g, i) => g.source !== sources[i]);
    if (stale) {
      teardown();
      for (const source of sources) {
        // The ghost's OWN BufferGeometry: heavy attributes shared by
        // reference, the source's real `color` (if any) deliberately left
        // out, and an RGBA color of our own — see the module header for the
        // two Codex P1s this design answers.
        const src = source.geometry;
        const geometry = new BufferGeometry();
        geometry.setIndex(src.getIndex());
        for (const name of Object.keys(src.attributes)) {
          if (name === 'color') continue;
          geometry.setAttribute(name, src.getAttribute(name)!);
        }
        const count = src.getAttribute('position').count;
        geometry.setAttribute('color', new Float32BufferAttribute(new Float32Array(count * 4), 4));
        // Morph deltas ride along by reference so the ghost deforms with the
        // flesh it diagnoses (Codex P2, #98) — influence STATE is shared on
        // the mesh below.
        geometry.morphAttributes = src.morphAttributes;
        geometry.morphTargetsRelative = src.morphTargetsRelative;
        const material = new MeshBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity,
          depthWrite: false,
          side: DoubleSide,
          // Sit on the flesh without z-fighting it.
          polygonOffset: true,
          polygonOffsetFactor: -2,
          polygonOffsetUnits: -2,
        });
        const ghost = new SkinnedMesh(geometry, material);
        // The ghost carries NO scene-graph name (Codex P2, #98 round eleven):
        // Three's animation binding resolves tracks by NAME, first match in
        // traversal order, so ANY name we pick could steal a track from an
        // imported node wearing the same one. Identity is the WeakSet stamp;
        // the reserved name survives only as the label reports use.
        markViewportDebris(ghost, 'weight-overlay');
        ghost.renderOrder = (source.renderOrder ?? 0) + 1;
        // Same skeleton, same bind — the ghost rides the playing clip for free.
        ghost.skeleton = source.skeleton;
        ghost.bindMode = source.bindMode;
        ghost.bind(source.skeleton, source.bindMatrix);
        ghost.frustumCulled = false; // the source's culling volume answers for both
        // Morph STATE is shared by array reference: tracks bind to the SOURCE
        // mesh's name, so a ghost with its own zeroed influences would stay in
        // the base shape while the flesh morphs out from under the heat.
        if (source.morphTargetInfluences) ghost.morphTargetInfluences = source.morphTargetInfluences;
        if (source.morphTargetDictionary) ghost.morphTargetDictionary = source.morphTargetDictionary;
        // A CHILD of the source, not a sibling (two Codex P2s, #98 round
        // three): a clip may animate the mesh NODE's own TRS, and a variant
        // or LOD may hide it — a sibling holding a one-time TRS copy detaches
        // from both. As a child at identity, the ghost inherits the source's
        // live transform and effective visibility every frame, for free.
        source.add(ghost);
        ghosts.push({ source, mesh: ghost, material });
      }
    }
    return null;
  }

  function paintBone(boneName: string, companions: readonly string[]): string | null {
    let boneSeen = false;
    let touched = 0;
    for (const g of ghosts) {
      const geometry = g.source.geometry;
      // The color lives on the GHOST's geometry — the subject's is never painted.
      const color = g.mesh.geometry.getAttribute('color') as BufferAttribute;
      const skinIndex = geometry.getAttribute('skinIndex') as BufferAttribute | undefined;
      const skinWeight = geometry.getAttribute('skinWeight') as BufferAttribute | undefined;
      const boneIndex = g.source.skeleton.bones.findIndex((b) => b.name === boneName);
      if (boneIndex >= 0) boneSeen = true;
      // The refusal keys on the PRIMARY alone: companions are derived bones
      // that simply may not exist on this rig.
      const indices = new Set<number>(boneIndex >= 0 ? [boneIndex] : []);
      for (const name of companions) {
        const idx = g.source.skeleton.bones.findIndex((b) => b.name === name);
        if (idx >= 0) indices.add(idx);
      }
      const count = color.count;
      for (let v = 0; v < count; v++) {
        let w = 0;
        if (indices.size > 0 && skinIndex && skinWeight) {
          for (let s = 0; s < 4; s++) {
            if (indices.has(skinIndex.getComponent(v, s))) w += skinWeight.getComponent(v, s);
          }
        }
        if (w > 0) touched++;
        const c = heat(w);
        color.setXYZW(v, c.r, c.g, c.b, opacity >= FLAT_OPACITY && w > 0 ? 1 : c.a);
      }
      color.needsUpdate = true;
    }
    if (!boneSeen) return `bone "${boneName}" is not in the skeleton`;
    if (touched === 0) return `bone "${boneName}" skins no vertices on this mesh`;
    influenced = touched;
    return null;
  }

  function paintShared(): void {
    influenced = 0;
    // Hues are keyed by BONE IDENTITY, not by each mesh's local skin index
    // (Codex P2, #98 round three): two skins can order their joint arrays
    // differently, and a local-index hue paints one bone two colours across
    // body and clothing while unrelated bones share one. First-seen name
    // order keeps the palette stable for a given subject — and identical to
    // the old behaviour on the single-skin case.
    const hueIndexByBone = new Map<string, number>();
    const paletteFor = (g: Ghost): Color[] =>
      g.source.skeleton.bones.map((bone) => {
        let idx = hueIndexByBone.get(bone.name);
        if (idx === undefined) {
          idx = hueIndexByBone.size;
          hueIndexByBone.set(bone.name, idx);
        }
        return boneHue(idx);
      });
    for (const g of ghosts) {
      const geometry = g.source.geometry;
      // Same rule as paintBone: the ghost's color, never the subject's.
      const color = g.mesh.geometry.getAttribute('color') as BufferAttribute;
      const skinIndex = geometry.getAttribute('skinIndex') as BufferAttribute | undefined;
      const skinWeight = geometry.getAttribute('skinWeight') as BufferAttribute | undefined;
      const palette = paletteFor(g);
      const count = color.count;
      for (let v = 0; v < count; v++) {
        let r = 0;
        let gr = 0;
        let b = 0;
        let total = 0;
        if (skinIndex && skinWeight) {
          for (let s = 0; s < 4; s++) {
            const w = skinWeight.getComponent(v, s);
            if (w <= 0) continue;
            const local = skinIndex.getComponent(v, s);
            const hue = palette[local] ?? boneHue(local);
            r += hue.r * w;
            gr += hue.g * w;
            b += hue.b * w;
            total += w;
          }
        }
        if (total > 0) {
          influenced++;
          color.setXYZW(v, r / total, gr / total, b / total, opacity >= FLAT_OPACITY ? 1 : 0.9);
        } else {
          color.setXYZW(v, 0, 0, 0, 0);
        }
      }
      color.needsUpdate = true;
    }
  }

  return {
    showBone(boneName: string, companions: readonly string[] = []): string | null {
      const refusal = ensureGhosts();
      if (refusal) {
        this.hide();
        return refusal;
      }
      const painted = paintBone(boneName, companions);
      if (painted) {
        // §7: a refusal never leaves a stale overlay standing for an answer.
        this.hide();
        return painted;
      }
      mode = 'bone';
      shownBone = boneName;
      shownCompanions = companions;
      for (const g of ghosts) g.mesh.visible = true;
      return null;
    },
    showShared(): string | null {
      const refusal = ensureGhosts();
      if (refusal) {
        this.hide();
        return refusal;
      }
      paintShared();
      // An all-transparent overlay claiming success is a blank standing for
      // an answer (§7; Codex P2, #98 round eight): a subject whose skins
      // carry no weight at all has nothing to show, and must say so.
      if (influenced === 0) {
        this.hide();
        return 'no vertex carries any skin weight — nothing to diagnose';
      }
      mode = 'shared';
      shownBone = null;
      for (const g of ghosts) g.mesh.visible = true;
      return null;
    },
    setOpacity(value: number): void {
      const wasFlat = opacity >= FLAT_OPACITY;
      opacity = Math.min(1, Math.max(MIN_OPACITY, value));
      for (const g of ghosts) g.material.opacity = opacity;
      // Crossing the flat threshold changes what the PAINT means (see
      // FLAT_OPACITY) — repaint the live mode so the dial keeps its promise.
      if (wasFlat !== opacity >= FLAT_OPACITY) {
        if (mode === 'bone' && shownBone !== null) void paintBone(shownBone, shownCompanions);
        else if (mode === 'shared') paintShared();
      }
    },
    hide(): void {
      mode = 'off';
      shownBone = null;
      // The census is LIVE evidence — an off (or refused) overlay reporting a
      // count from an earlier selection is a stale answer (Codex P2, #98
      // round eight; refusal paths land here too).
      influenced = 0;
      for (const g of ghosts) g.mesh.visible = false;
    },
    dispose(): void {
      teardown();
      mode = 'off';
      shownBone = null;
    },
    probe() {
      return {
        mode,
        bone: shownBone,
        opacity,
        ghosts: ghosts.length,
        influenced,
      };
    },
  };
}

/** Test seam: the paint math alone, for fixtures that never build a scene. */
export const __weightOverlayInternals = { heat, boneHue };
